import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('close', this.#onNativeDialogClose);
    }

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    const { dialog } = this.refs;
    if (dialog instanceof HTMLDialogElement) {
      dialog.removeEventListener('close', this.#onNativeDialogClose);
    }
    this.#unlockBodyScroll();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    super.disconnectedCallback();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** Clears scroll lock applied in showDialog (safe to call repeatedly). */
  #unlockBodyScroll() {
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Fires whenever the native dialog closes (including programmatic close()).
   * Keeps body scroll / top layer in sync if closeDialog() was skipped or interrupted.
   */
  #onNativeDialogClose = () => {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#unlockBodyScroll();
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.refs.dialog?.classList.remove('dialog-closing');
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      // Non-modal: page outside the dialog stays interactive (header bag X, menu, etc.).
      // showModal() applies inert to non-descendants, which includes the cart trigger beside <dialog>.
      if (this.hasAttribute('dialog-non-modal')) {
        dialog.show();
      } else {
        dialog.showModal();
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    const wasOpen = dialog.open;

    try {
      if (wasOpen) {
        // Force browser to restart animation by resetting it
        dialog.style.animation = 'none';
        void dialog.offsetWidth;
        dialog.classList.add('dialog-closing');
        dialog.style.animation = '';

        const animationDone = new Promise((resolve) => {
          requestAnimationFrame(() => {
            onAnimationEnd(dialog, resolve, { subtree: false });
          });
        });
        const maxWait = new Promise((resolve) => {
          setTimeout(resolve, 500);
        });

        await Promise.race([animationDone, maxWait]);

        dialog.classList.remove('dialog-closing');
        dialog.close();
        // DialogCloseEvent is dispatched from #onNativeDialogClose (sync with dialog.close())
      }
    } finally {
      // If the dialog was already closed (e.g. after a morph) but body stayed position:fixed, recover.
      this.#unlockBodyScroll();
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
